import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { updateFrameworkVisibility } from 'services/transparency-config-service';
import { getFrameworksInUse } from 'services/gap-analysis';
import { DotsLoader } from 'Components/DotsLoader';
import { Dialog, Checkbox } from 'Components/ui';
import styled from 'styled-components';
import { Skeleton } from 'Components/common/Skeleton';

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const Title = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 20px;
  line-height: 1.6;
`

const Divider = styled.div`
  height: 1px;
  background: var(--vo-cloud);
  margin: 20px 0;
`

const TableBorder = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHead = styled.thead`
  background: var(--vo-mist);
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`

const TableCell = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  text-align: ${({ $align }) => $align || 'left'};
`

const TableHeaderCell = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`

const HeaderMultiline = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: center;
  border-bottom: 1px solid var(--vo-cloud);
`

const HeaderSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  color: var(--vo-steel);
  margin: 2px 0 0;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`

const CellText = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`

const CellSubtext = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`

const CenterFlex = styled.div`
  display: flex;
  gap: 2px;
  justify-content: center;
`

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  &:hover:not(:disabled) { background: var(--vo-mist); color: var(--vo-celeste); }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`

const SaveButton = styled.button`
  height: 44px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { background: var(--vo-cobalto); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`

const AlertError = styled(Alert)`
  background-color: var(--vo-danger-10);
  color: var(--vo-danger);
  font-family: 'Lato', sans-serif;
`

const AlertInfo = styled(Alert)`
  background-color: var(--vo-info-8);
  color: var(--vo-celeste);
  font-family: 'Lato', sans-serif;
`

const ArrowUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
)

const ArrowDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

export const FrameworkVisibility = ({ config, onUpdate, companyKey, companyId }) => {
  const { t } = useTranslation(['transparency', 'common']);

  const { data: frameworksData, isLoading: loadingFrameworks, error: frameworksError } = useQuery(
    ['frameworksInUse', companyId],
    () => getFrameworksInUse(companyId),
    {
      enabled: !!companyId,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      frameworks: [],
    },
  });

  const { fields, move } = useFieldArray({
    control,
    name: 'frameworks',
  });

  useEffect(() => {
    if (frameworksData?.data && Array.isArray(frameworksData.data)) {
      const savedConfig = Array.isArray(config?.frameworkVisibility)
        ? config.frameworkVisibility
        : [];

      const enriched = frameworksData.data.map((fw, index) => {
        const existing = savedConfig.find(s => s.frameworkId === fw._id);
        return {
          frameworkId: fw._id,
          frameworkName: fw.name,
          frameworkSource: fw.source || '',
          frameworkGeo: fw.geo || fw.geography || '',
          showBadgePublicly: existing?.showBadgePublicly ?? false,
          showMaturityProtected: existing?.showMaturityProtected ?? false,
          displayOrder: existing?.displayOrder ?? index,
        };
      }).sort((a, b) => a.displayOrder - b.displayOrder);

      setValue('frameworks', enriched);
    }
  }, [frameworksData, config, setValue]);

  const mutation = useMutation(
    (data) => updateFrameworkVisibility(companyKey, data.frameworks),
    {
      onSuccess: () => {
        Dialog.alert({
          title: t('transparency:frameworkVisibility.notifications.success'),
          message: t('transparency:frameworkVisibility.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        Dialog.alert({
          title: t('transparency:frameworkVisibility.notifications.error'),
          message: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    const frameworks = data.frameworks.map((fw, index) => ({
      ...fw,
      displayOrder: index,
    }));
    mutation.mutate({ frameworks });
  };

  const moveUp = (index) => {
    if (index > 0) {
      move(index, index - 1);
    }
  };

  const moveDown = (index) => {
    if (index < fields.length - 1) {
      move(index, index + 1);
    }
  };

  if (loadingFrameworks) {
    return (
      <Card>
        <Skeleton $width={300} $height={40} $marginBottom={8} />
        <Skeleton $width={'100%'} $height={20} $marginBottom={16} />
        <Skeleton $width={'100%'} $height={200} />
      </Card>
    );
  }

  if (frameworksError) {
    return (
      <Card>
        <AlertError>{t('transparency:frameworkVisibility.error')}</AlertError>
      </Card>
    );
  }

  if (!frameworksData?.data || frameworksData.data.length === 0) {
    return (
      <Card>
        <Title>{t('transparency:frameworkVisibility.title')}</Title>
        <AlertInfo>{t('transparency:frameworkVisibility.empty')}</AlertInfo>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Title>{t('transparency:frameworkVisibility.title')}</Title>
        <Subtitle>{t('transparency:frameworkVisibility.subtitle')}</Subtitle>

        <Divider />

        <TableBorder>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>{t('transparency:frameworkVisibility.table.framework')}</TableHeaderCell>
                <HeaderMultiline>
                  <span>{t('transparency:frameworkVisibility.table.showBadge')}</span>
                  <HeaderSub>{t('transparency:frameworkVisibility.table.badgeLevel')}</HeaderSub>
                </HeaderMultiline>
                <HeaderMultiline>
                  <span>{t('transparency:frameworkVisibility.table.showMaturity')}</span>
                  <HeaderSub>{t('transparency:frameworkVisibility.table.maturityLevel')}</HeaderSub>
                </HeaderMultiline>
                <TableHeaderCell $align="center">{t('transparency:frameworkVisibility.table.order')}</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <CellText>{field.frameworkName}</CellText>
                    <CellSubtext>{field.frameworkSource && field.frameworkGeo ? `${field.frameworkSource} · ${field.frameworkGeo}` : ''}</CellSubtext>
                  </TableCell>
                  <TableCell $align="center">
                    <Controller
                      control={control}
                      name={`frameworks.${index}.showBadgePublicly`}
                      render={({ field: { onChange, value } }) => (
                        <Checkbox checked={value} onChange={onChange} />
                      )}
                    />
                  </TableCell>
                  <TableCell $align="center">
                    <Controller
                      control={control}
                      name={`frameworks.${index}.showMaturityProtected`}
                      render={({ field: { onChange, value } }) => (
                        <Checkbox checked={value} onChange={onChange} />
                      )}
                    />
                  </TableCell>
                  <TableCell $align="center">
                    <CenterFlex>
                      <IconBtn onClick={() => moveUp(index)} disabled={index === 0} type="button" aria-label={t('transparency:frameworkVisibility.moveUp')}>
                        <ArrowUp />
                      </IconBtn>
                      <IconBtn onClick={() => moveDown(index)} disabled={index === fields.length - 1} type="button" aria-label={t('transparency:frameworkVisibility.moveDown')}>
                        <ArrowDown />
                      </IconBtn>
                    </CenterFlex>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableBorder>

        <Actions>
          <SaveButton type="submit" disabled={mutation.isLoading}>
            {mutation.isLoading ? <DotsLoader /> : t('common:buttons.save')}
          </SaveButton>
        </Actions>
      </form>
    </Card>
  );
};
